import React, { useMemo } from 'react';

export interface AppHeaderCloudSyncBadgeProps {
  externalSyncStatusText?: string;
  pendingSyncCount: number;
  syncError: any;
  isSyncing: boolean;
  isOnline: boolean;
  lastSynced?: number | string | null;
}

export const AppHeaderCloudSyncBadge: React.FC<AppHeaderCloudSyncBadgeProps> = ({
  externalSyncStatusText,
  pendingSyncCount,
  syncError,
  isSyncing,
  isOnline,
  lastSynced,
}) => {
  const syncStatusText = useMemo(() => {
    if (externalSyncStatusText) return externalSyncStatusText;
    if (pendingSyncCount > 0) {
      return `${pendingSyncCount} ${pendingSyncCount === 1 ? 'Änderung' : 'Änderungen'} ausstehend`;
    }
    if (syncError) return typeof syncError === 'string' ? syncError : 'Synchronisationsfehler';
    if (isSyncing) return 'Aktualisierung...';
    if (!lastSynced) return 'Noch nicht synchronisiert';

    const d = new Date(lastSynced);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const datePrefix = isToday
      ? 'Heute'
      : isYesterday
        ? 'Gestern'
        : d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
    return `${datePrefix} ${d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`;
  }, [externalSyncStatusText, pendingSyncCount, syncError, isSyncing, lastSynced]);

  return (
    <div className="group relative flex items-center shrink-0 cursor-help">
      <div className="relative flex h-3.5 w-3.5 items-center justify-center">
        {(isSyncing || pendingSyncCount > 0 || !isOnline || syncError) && (
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
              !isOnline || syncError ? 'bg-rose-400/50' : 'bg-amber-400/50'
            }`}
          />
        )}
        <span
          className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
            !isOnline || syncError
              ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.8)]'
              : pendingSyncCount > 0 || isSyncing
                ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)] animate-pulse'
                : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
          }`}
        />
      </div>

      {/* Tooltip bei Mouseover */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 px-2.5 py-1.5 bg-slate-900 text-white text-[10px] font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[200] shadow-2xl border border-white/10 flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              !isOnline || syncError
                ? 'bg-rose-500'
                : pendingSyncCount > 0 || isSyncing
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
            }`}
          />
          <span className="uppercase tracking-wider">
            {!isOnline
              ? 'Offline'
              : syncError
                ? 'Cloud Fehler'
                : pendingSyncCount > 0
                  ? 'Lokal gespeichert'
                  : isSyncing
                    ? 'Synchronisiere...'
                    : 'Synchronisiert'}
          </span>
        </div>
        {syncStatusText && (
          <span className="text-[9px] text-slate-300 font-medium tabular-nums">
            {syncStatusText}
          </span>
        )}
        <span className="text-[8px] font-normal text-slate-400">
          {!isOnline
            ? 'Keine Internetverbindung'
            : syncError
              ? 'Fehler beim Abgleich mit Firebase'
              : pendingSyncCount > 0
                ? `${pendingSyncCount} Änderung(en) warten auf Cloud-Sync`
                : isSyncing
                  ? 'Daten werden hochgeladen...'
                  : 'Alle Daten sind synchronisiert'}
        </span>
      </div>
    </div>
  );
};
